import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink } from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

type InstagramVariant = "survival-badge" | "countdown";

interface InstagramPostGraphicProps {
  variant?: InstagramVariant;
}

const InstagramPostGraphic: React.FC<InstagramPostGraphicProps> = ({ variant = "survival-badge" }) => {
  const graphicRef = useRef<HTMLDivElement>(null);
  const today = new Date();

  const downloadGraphic = async () => {
    if (graphicRef.current) {
      try {
        const loadingToast = toast.loading("Preparing your Instagram post...");
        
        const canvas = await html2canvas(graphicRef.current, {
          backgroundColor: "#FFFFFF",
          scale: 3,
          logging: false,
          allowTaint: true,
          useCORS: true,
        });
        
        const dataUrl = canvas.toDataURL('image/png');
        
        const link = document.createElement('a');
        link.download = `saunadip-instagram-${variant}.png`;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.dismiss(loadingToast);
        toast.success("Instagram post downloaded successfully!");
      } catch (error) {
        console.error("Download error:", error);
        toast.error("Failed to download. Please try again.");
      }
    }
  };

  const handleVisitSite = () => {
    window.open("https://saunadip.com/", "_blank");
  };

  // Render Survival Badge variant
  if (variant === "survival-badge") {
    return (
      <div className="w-full max-w-2xl mx-auto">
        {/* Instagram Post Container - Square 1:1 ratio */}
        <div 
          ref={graphicRef} 
          className="w-full aspect-square bg-white p-8 relative overflow-hidden rounded-3xl"
        >
          <div className="h-full flex flex-col items-center justify-between z-10 relative">
            {/* Top Section - I SURVIVED */}
            <div className="w-full flex flex-col items-center">
              <h1 className="text-6xl font-bold text-navy-900 mb-1">
                I SURVIVED
              </h1>
              <p className="text-xl uppercase tracking-wide text-navy-900 mb-8">
                A JOURNEY THROUGH FIRE AND ICE
              </p>
            </div>
            
            {/* Badge Circle - Updated text sizes */}
            <div className="relative mb-10">
              {/* Circle Border */}
              <div className="w-[300px] h-[300px] rounded-full border-8 border-gray-200 relative overflow-hidden">
                {/* Hot temperature section - top half */}
                <div className="absolute top-0 left-0 right-0 h-1/2 rounded-t-full overflow-hidden">
                  <div className="absolute inset-0 bg-sauna-orange flex flex-col items-center justify-center">
                    <div className="text-6xl font-bold text-white">70°C</div>
                    <Badge variant="temperature" className="mt-1 py-1 text-xs">
                      TOASTY LEVEL
                    </Badge>
                  </div>
                </div>
                
                {/* Cold temperature section - bottom half */}
                <div className="absolute bottom-0 left-0 right-0 h-1/2 rounded-b-full overflow-hidden">
                  <div className="absolute inset-0 bg-sauna-blue flex flex-col items-center justify-center">
                    <Badge variant="temperature" className="mb-1 py-1 text-xs">
                      COOL CHALLENGER
                    </Badge>
                    <div className="text-6xl font-bold text-white">15°C</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Name in large font - Updated to @Location format */}
            <div className="flex flex-col items-center">
              <h2 className="text-5xl font-bold text-navy-900 text-center mb-1">
                @SaunaDip
              </h2>
              
              <p className="text-2xl text-gray-500 mb-10">
                {format(today, "MMMM d, yyyy")}
              </p>
              
              {/* Action Buttons in line with the design */}
              <div className="grid grid-cols-2 gap-4 w-full max-w-md">
                <Button 
                  className="bg-white hover:bg-gray-100 text-navy-900 border-2 border-gray-300 rounded-md font-semibold"
                  variant="outline"
                >
                  Download Badge
                </Button>
                
                <Button 
                  className="bg-sauna-orange hover:bg-orange-600 text-white rounded-md font-semibold"
                >
                  Share on Instagram
                </Button>
              </div>
              
              <p className="text-center text-gray-500 mt-6 text-lg">
                Tag us @SaunaDip with your badge!
              </p>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-6">
          <Button 
            onClick={downloadGraphic}
            className="bg-sauna-orange hover:bg-orange-600 text-white font-semibold"
          >
            <Download size={18} className="mr-2" />
            Download for Instagram
          </Button>
          
          <Button 
            onClick={handleVisitSite}
            className="bg-sauna-blue hover:bg-blue-600 text-white font-semibold"
          >
            <ExternalLink size={18} className="mr-2" />
            Visit saunadip.com
          </Button>
        </div>
        
        <p className="text-sm text-gray-500 max-w-md text-center mx-auto mt-4">
          This is a 1:1 square format optimized for Instagram posts. Download the image and upload it to your Instagram account.
        </p>
      </div>
    );
  }
  
  // Render Countdown variant
  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Instagram Post Container - Square 1:1 ratio */}
      <div 
        ref={graphicRef} 
        className="w-full aspect-square bg-white p-8 relative overflow-hidden rounded-3xl"
      >
        <div className="h-full flex flex-col items-center justify-between text-center z-10 relative">
          {/* Top Section - THE COUNTDOWN IS ON */}
          <div className="w-full mt-8">
            <h2 className="text-4xl font-bold text-navy-900">
              THE COUNTDOWN IS ON
            </h2>
          </div>
          
          {/* Middle Section - Countdown Circle */}
          <div className="relative my-6">
            <div className="w-64 h-64 rounded-full border-8 border-orange-500 bg-blue-500 flex flex-col items-center justify-center shadow-lg">
              <div className="text-5xl font-bold text-white mb-2">10 DAYS</div>
              <div className="text-2xl font-bold text-white mb-1">UNTIL LAUNCH</div>
              <Badge variant="temperature" className="mt-2 px-4 py-1 uppercase font-bold">
                MAY 15, 2025
              </Badge>
            </div>
          </div>
          
          {/* Bottom Section - Find Your Perfect... */}
          <div className="w-full mb-8">
            <h3 className="text-2xl font-bold text-navy-900">
              Find Your Perfect Hot & Cold Escape
            </h3>
            <p className="text-xl text-gray-600 mt-4 max-w-md mx-auto">
              Ireland & UK's first sauna and plunge directory launching soon!
            </p>
          </div>
        </div>
        
        {/* CTA Banner at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-orange-500 to-blue-500 py-3 text-center">
          <p className="text-white text-xl font-bold">
            Follow our journey at saunadip.com
          </p>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-center gap-4 mt-6">
        <Button 
          onClick={downloadGraphic}
          className="bg-sauna-orange hover:bg-orange-600 text-white font-semibold"
        >
          <Download size={18} className="mr-2" />
          Download for Instagram
        </Button>
        
        <Button 
          onClick={handleVisitSite}
          className="bg-sauna-blue hover:bg-blue-600 text-white font-semibold"
        >
          <ExternalLink size={18} className="mr-2" />
          Visit saunadip.com
        </Button>
      </div>
      
      <p className="text-sm text-gray-500 max-w-md text-center mx-auto mt-4">
        This is a 1:1 square format optimized for Instagram posts. Download the image and upload it to your Instagram account.
      </p>
    </div>
  );
};

export default InstagramPostGraphic;
